/**
 * Who is browsing, as far as GA4 may know: signed in or not, bought before or
 * not. Never an email, a name or a user id — the server's Measurement
 * Protocol sends user_id itself.
 *
 * After sign-in the attribution payload also goes to site_server
 * (POST /api/analytics/attribution → profile.attribution), so a later Google
 * Play / App Store purchase on the server can be credited to the ad click.
 * Fire-and-forget, once per tab session and owner, again when a new click id
 * arrives. The first one in a tab waits up to 2 s for GA's _ga cookie (gtm.js
 * sets it a moment after the page loads): the cookie's client id is part of
 * what was sent, so sending without it and again with it was two POSTs.
 */
import { siteSessionToken } from "@/shared/api/adminHeaders";
import { BACKEND_API_URL } from "@/shared/config/app";
import { clickSignature, getAttributionPayload, getGaClientId } from "./attribution";
import { pushRaw } from "./dataLayer";
import { isInternalTraffic } from "./internal";
import { gtmOnPage } from "./navigate";

export interface AnalyticsUser {
    is_logged_in: boolean;
    has_purchase?: boolean;
}

let lastUser: { is_logged_in: string; has_purchase?: string } | null = null;

/**
 * Pushes `user_update` with user_is_logged_in / user_has_purchase ("1"/"0"),
 * which GTM maps to GA4 user properties (GTM_NOTES.md). Pushes only on change;
 * has_purchase left out keeps the previous value.
 */
export const setAnalyticsUser = (u: AnalyticsUser): void => {
    const next = {
        is_logged_in: u.is_logged_in ? "1" : "0",
        has_purchase:
            u.has_purchase === undefined
                ? u.is_logged_in
                    ? lastUser?.has_purchase
                    : undefined
                : u.has_purchase
                  ? "1"
                  : "0",
    };
    if (lastUser && lastUser.is_logged_in === next.is_logged_in && lastUser.has_purchase === next.has_purchase) {
        return;
    }
    lastUser = next;
    pushRaw({
        event: "user_update",
        user_is_logged_in: next.is_logged_in,
        user_has_purchase: next.has_purchase,
        ...(isInternalTraffic() ? { traffic_type: "internal" } : {}),
    });
};

export const ATTRIBUTION_SENT_KEY = "analytics_attr_sent";
export const attributionUrl = (): string => `${BACKEND_API_URL}/api/analytics/attribution`;

let inflight: string | null = null;

export const GA_COOKIE_WAIT_MS = 2000;
const GA_COOKIE_POLL_MS = 100;
let gaWait: Promise<void> | null = null;

/** Resolves once the _ga cookie is there, or after `ms`; at once without GTM. */
const waitForGaClientId = (ms: number): Promise<void> =>
    new Promise((resolve) => {
        if (getGaClientId() || !gtmOnPage()) {
            resolve();
            return;
        }
        const started = Date.now();
        const tick = () => {
            if (getGaClientId() || Date.now() - started >= ms) resolve();
            else setTimeout(tick, GA_COOKIE_POLL_MS);
        };
        setTimeout(tick, GA_COOKIE_POLL_MS);
    });

/**
 * POST /api/analytics/attribution for the signed-in owner, unless this tab
 * already sent it for the same owner and the same click ids. `owner` is used
 * only for that local comparison (sessionStorage), never sent anywhere.
 * Resolves true when a request was made and accepted.
 */
export const syncAttributionToServer = async (owner: string | null | undefined): Promise<boolean> => {
    const who = (owner || "").trim().toLowerCase();
    if (!who || !siteSessionToken()) return false;
    // Every call in the tab shares the one wait, so concurrent first calls
    // resume together and the inflight check below keeps them to one POST.
    if (!gaWait) gaWait = waitForGaClientId(GA_COOKIE_WAIT_MS);
    await gaWait;
    const token = siteSessionToken();
    if (!token) return false;
    const sig = `${who}|${clickSignature()}|${getGaClientId() || ""}`;
    try {
        if (sessionStorage.getItem(ATTRIBUTION_SENT_KEY) === sig) return false;
    } catch {
        // no sessionStorage — send, the server keeps only the latest anyway
    }
    if (inflight === sig) return false;
    const attribution = getAttributionPayload();
    if (!attribution) return false;
    inflight = sig;
    try {
        const res = await fetch(attributionUrl(), {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ attribution }),
            keepalive: true,
        });
        if (!res.ok) return false;
        try {
            sessionStorage.setItem(ATTRIBUTION_SENT_KEY, sig);
        } catch {
            // see above
        }
        return true;
    } catch {
        return false;
    } finally {
        inflight = null;
    }
};

/** Tests only. */
export const resetAnalyticsUserState = (): void => {
    lastUser = null;
    inflight = null;
    gaWait = null;
};
